import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, CheckCircle, XCircle, X, MoveRight } from 'lucide-react';

const TimelineGame = ({ config, onEnd }) => {
    const [events, setEvents] = useState([]); // L'ordre correct
    const [shuffledEvents, setShuffledEvents] = useState([]); // Les choix
    const [selectedOrder, setSelectedOrder] = useState([]); 
    const [isChecked, setIsChecked] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [score, setScore] = useState(0);
    const [round, setRound] = useState(0);

    const startNewRound = useCallback(() => {
        if (!config.events || config.events.length < 3) return;
        
        const pool = [...config.events].sort(() => 0.5 - Math.random()).slice(0, 3);
        const correctOrder = [...pool].sort((a, b) => a.year - b.year);
        
        setEvents(correctOrder);
        setShuffledEvents([...pool].sort(() => 0.5 - Math.random()));
        setSelectedOrder([]);
        setIsChecked(false);
        setIsCorrect(false);
    }, [config.events]);

    useEffect(() => {
        startNewRound();
    }, [round, startNewRound]);

    const handleSelect = (event) => {
        if (isChecked || selectedOrder.includes(event)) return;
        if (selectedOrder.length < 3) {
            setSelectedOrder([...selectedOrder, event]);
        }
    };

    const handleRemove = (eventToRemove) => {
        if (isChecked) return;
        setSelectedOrder(selectedOrder.filter(e => e !== eventToRemove));
    };

    const checkAnswer = () => {
        if (selectedOrder.length !== 3) return;
        const isWin = JSON.stringify(selectedOrder.map(e => e.year)) === JSON.stringify(events.map(e => e.year));
        setIsCorrect(isWin);
        setIsChecked(true);
        if (isWin) setScore(score + 1);
    };

    const handleNext = () => {
        if (round < 4) {
            setRound(round + 1);
        } else {
            onEnd(score);
        }
    };

    if (round >= 5) return null;

    return (
        <div className="timeline-container">
            <h2 className="timeline-instruction">Remettez ces événements dans l'ordre</h2>
            <p className="timeline-round-info">Manche {round + 1} / 5</p>

            {/* LA FRISE (Zone de dépôt) */}
            <div className="timeline-main-display">
                <div className="timeline-frise">
                    { [0, 1, 2].map((index) => {
                        const item = selectedOrder[index];
                        const isError = isChecked && item !== events[index];
                        const isRight = isChecked && item === events[index];

                        return (
                            <React.Fragment key={index}>
                                <div 
                                    className={`timeline-slot ${!item ? 'empty' : ''} ${isError ? 'wrong' : ''} ${isRight ? 'correct' : ''}`}
                                    onClick={() => item && handleRemove(item)}
                                >
                                    {item ? (
                                        <div className="slot-content">
                                            <span className="event-label">{item.label}</span>
                                            {isChecked && <span className="event-year">{item.year}</span>}
                                            {!isChecked && <X className="remove-icon" size={16} />}
                                        </div>
                                    ) : (
                                        <span className="placeholder">{index + 1}</span>
                                    )}
                                </div>
                                {index < 2 && <ArrowRight className="timeline-sep" />}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* ZONE DE CORRECTION (Apparaît seulement si erreur) */}
                {isChecked && !isCorrect && (
                    <div className="correction-area">
                        <div className="correction-label"><MoveRight size={20} /> Solution correcte :</div>
                        <div className="timeline-frise solution">
                            {events.map((event, idx) => (
                                <React.Fragment key={idx}>
                                    <div className="timeline-slot solution-item">
                                        <span className="event-label">{event.label}</span>
                                        <span className="event-year">{event.year}</span>
                                    </div>
                                    {idx < 2 && <ArrowRight className="timeline-sep" />}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ZONE DE CHOIX */}
            {!isChecked && (
                <div className="timeline-choices">
                    {shuffledEvents.map((event, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleSelect(event)}
                            disabled={selectedOrder.includes(event)}
                            className="choice-button"
                        >
                            {event.label}
                        </button>
                    ))}
                </div>
            )}

            {/* ACTIONS */}
            <div className="timeline-actions">
                {!isChecked ? (
                    <button 
                        onClick={checkAnswer} 
                        className="btn-validate"
                        disabled={selectedOrder.length < 3}
                    >
                        Vérifier
                    </button>
                ) : (
                    <div className="post-check">
                        <div className={`feedback-msg ${isCorrect ? 'success' : 'error'}`}>
                            {isCorrect ? <><CheckCircle /> Bravo !</> : <><XCircle /> Analyse de l'erreur...</>}
                        </div>
                        <button onClick={handleNext} className="btn-next">
                            Continuer
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TimelineGame;